/**
 * RNNoise 노이즈 제거 설정
 *
 * RNNoise WASM 모델 자체는 런타임 파라미터 튜닝이 불가하지만,
 * Web Audio API 노드 체인으로 전/후처리를 추가하여 실질적 조절이 가능하다.
 *
 * 오디오 처리 그래프:
 *
 *                    ┌─ wetGain ── NoiseSuppressorWorklet ─┐
 *   Source ──────────┤                                     ├── [HighPass] ── [LowPass] ── [Compressor] ── outputGain ── Destination
 *                    └─ dryGain ──────────────────────────┘
 *
 * - mixRatio가 1.0이면 RNNoise만, 0.0이면 원본만 통과
 * - 중간값이면 원본과 노이즈제거 신호를 블렌딩
 */

export interface NoiseSuppressionConfig {
  /** 기본 활성화 여부 (회의 입장 시 자동 적용) */
  enabled: boolean;

  /**
   * 원본/노이즈제거 믹스 비율
   * - 1.0 = 100% 노이즈 제거 (최대 억제, 음성이 약간 부자연스러울 수 있음)
   * - 0.7 = 70% 노이즈 제거 + 30% 원본 (자연스러운 톤 유지, 권장)
   * - 0.5 = 반반 믹스 (가벼운 노이즈 제거)
   * - 0.0 = 원본 그대로 (노이즈 제거 없음)
   */
  mixRatio: number;

  /**
   * 출력 게인 (0.0 ~ 2.0)
   * RNNoise가 음성 볼륨도 약간 감소시킬 수 있으므로 보정용.
   * - 1.0 = 변경 없음
   * - 1.2 = 20% 증폭 (RNNoise로 인한 음성 손실 보정)
   * - 0.8 = 20% 감쇠
   */
  outputGain: number;

  /**
   * 고역 통과 필터 (High-pass filter) 활성화
   * 저주파 노이즈(바람소리, 에어컨 진동, 책상 진동 등)를 차단한다.
   * RNNoise가 잡지 못하는 저주파 잡음에 효과적.
   */
  highPassFilter: boolean;

  /**
   * 고역 통과 필터 주파수 (Hz)
   * 이 값 이하의 주파수를 차단한다.
   * - 85 = 남성 음성 최저 기본 주파수(F0) 근처 (권장, 안전)
   * - 120 = 좀 더 공격적 차단 (저음 목소리가 약간 얇아질 수 있음)
   * - 200 = 매우 공격적 (저음 손실 있음, 바람소리 환경에서만 사용)
   */
  highPassFrequency: number;

  /**
   * 저역 통과 필터 (Low-pass filter) 활성화
   * 사람 음성 대역 밖의 고주파 히스, 팬 소리, 날카로운 잔향을 줄인다.
   */
  lowPassFilter: boolean;

  /**
   * 저역 통과 필터 주파수 (Hz)
   * - 7600 = 회의 음성 자연스러움과 고주파 노이즈 억제 균형
   * - 7000 = 더 공격적 억제
   */
  lowPassFrequency: number;

  /** RNNoise VAD 기반 음성 게이트 활성화 */
  voiceGate: boolean;

  /** 이 VAD 점수 이상이면 음성으로 판단해 게이트를 연다. */
  voiceGateOpenThreshold: number;

  /** 이 VAD 점수 이하가 지속되면 게이트를 닫는다. */
  voiceGateCloseThreshold: number;

  /** 게이트가 닫혔을 때도 남기는 최소 출력 게인. 0이면 무음에 가깝다. */
  voiceGateMinGain: number;

  /** 게이트가 열린 뒤 닫히기 전까지 유지하는 시간(ms). 첫 음절/어미 잘림 방지용. */
  voiceGateHangoverMs: number;

  /** 게이트가 열릴 때 목표 게인까지 올라가는 시간(ms). */
  voiceGateAttackMs: number;

  /** 게이트가 닫힐 때 최소 게인까지 내려가는 시간(ms). */
  voiceGateReleaseMs: number;

  /**
   * 다이나믹 압축 (Compressor) 활성화
   * 갑작스러운 큰 소리(기침, 키보드 충격)를 자동으로 억제한다.
   */
  compressor: boolean;

  /**
   * 컴프레서 임계값 (dB, 음수)
   * 이 값보다 큰 소리에 압축을 적용한다.
   * - -24 = 부드러운 압축 (권장)
   * - -18 = 공격적 압축 (큰소리를 강하게 억제)
   * - -30 = 매우 부드러운 압축
   */
  compressorThreshold: number;
}

/**
 * 기본 설정값.
 * 대부분의 회의 환경에서 안정적으로 동작하는 값으로 설정.
 */
export const DEFAULT_NOISE_SUPPRESSION_CONFIG: NoiseSuppressionConfig = {
  enabled: true,
  mixRatio: 1.0,
  outputGain: 1.0,
  highPassFilter: true,
  highPassFrequency: 100,
  lowPassFilter: true,
  lowPassFrequency: 7600,
  voiceGate: true,
  voiceGateOpenThreshold: 0.62,
  voiceGateCloseThreshold: 0.32,
  voiceGateMinGain: 0.06,
  voiceGateHangoverMs: 260,
  voiceGateAttackMs: 20,
  voiceGateReleaseMs: 220,
  compressor: true,
  compressorThreshold: -24,
};

/**
 * 프리셋: 사무실 환경 (에어컨, 키보드, 동료 대화)
 */
export const PRESET_OFFICE: Partial<NoiseSuppressionConfig> = {
  mixRatio: 0.85,
  outputGain: 1.1,
  highPassFilter: true,
  highPassFrequency: 100,
  lowPassFilter: true,
  lowPassFrequency: 7600,
  voiceGate: true,
  voiceGateOpenThreshold: 0.6,
  voiceGateCloseThreshold: 0.3,
  voiceGateMinGain: 0.07,
  voiceGateHangoverMs: 260,
  voiceGateAttackMs: 20,
  voiceGateReleaseMs: 220,
  compressor: true,
  compressorThreshold: -24,
};

/**
 * 프리셋: 조용한 환경 (자택, 회의실)
 * 가벼운 노이즈 제거, 자연스러운 음성 우선
 */
export const PRESET_QUIET: Partial<NoiseSuppressionConfig> = {
  mixRatio: 0.5,
  outputGain: 1.0,
  highPassFilter: false,
  lowPassFilter: false,
  voiceGate: true,
  voiceGateOpenThreshold: 0.58,
  voiceGateCloseThreshold: 0.28,
  voiceGateMinGain: 0.12,
  voiceGateHangoverMs: 300,
  voiceGateAttackMs: 18,
  voiceGateReleaseMs: 260,
  compressor: false,
};

/**
 * 프리셋: 소음 환경 (카페, 공공장소, 야외)
 * 최대한 공격적 노이즈 제거
 */
export const PRESET_NOISY: Partial<NoiseSuppressionConfig> = {
  mixRatio: 1.0,
  outputGain: 1.2,
  highPassFilter: true,
  highPassFrequency: 120,
  lowPassFilter: true,
  lowPassFrequency: 7000,
  voiceGate: true,
  voiceGateOpenThreshold: 0.68,
  voiceGateCloseThreshold: 0.36,
  voiceGateMinGain: 0.035,
  voiceGateHangoverMs: 240,
  voiceGateAttackMs: 15,
  voiceGateReleaseMs: 260,
  compressor: true,
  compressorThreshold: -18,
};

/**
 * 설정 병합 헬퍼.
 * 부분 설정을 기본값과 병합한다.
 */
export function mergeNoiseSuppressionConfig(
  partial?: Partial<NoiseSuppressionConfig>
): NoiseSuppressionConfig {
  return { ...DEFAULT_NOISE_SUPPRESSION_CONFIG, ...partial };
}
